import Head from 'next/head';
import type { ComponentType } from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import reset from 'styled-reset';

import { SetFAQStructuredData } from '../src/components/FAQs/FAQSection';
import * as fonts from '../src/index.css';
import { theme } from '../src/theme';

const GlobalStyle = createGlobalStyle`
    ${reset}
    ${fonts}
`;

const gtm = process.env.CARVANA_APP_GTM_ID;

type MyAppProps<T> = {
  Component: ComponentType<T>
  pageProps: T
}

const MyApp = <T extends object>({ Component, pageProps }: MyAppProps<T>) => (
  <>
    <Head>
      <meta charSet="utf-8" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=5,shrink-to-fit=no"
      />
      <meta
        name="description"
        data-cv-test="sellToCarvanaSeoDescription"
        content="Get a cash offer in under two minutes & get your car picked up from your driveway as soon as the same day. Learn how to sell or trade in your car online by getting your car's value by its VIN number."
      />
      <meta name="theme-color" content="#FFFFFF" />
      <title>Sell or Trade In Your Car Online - Carvana</title>
      <link rel="canonical" href="https://www.carvana.com/sell-my-car" />
      <link rel="manifest" href={`${process.env.CARVANA_APP_STC_SHELL_PUBLIC_URL}/manifest.json`} />

      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={`${process.env.CARVANA_APP_CDN_URL}/icons/apple/apple-touch-icon.png`}
        crossOrigin=""
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={`${process.env.CARVANA_APP_CDN_URL}/icons/favicon-32x32.png`}
        crossOrigin=""
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={`${process.env.CARVANA_APP_CDN_URL}/icons/favicon-16x16.png`}
        crossOrigin=""
      />
      <link
        rel="icon"
        type="image/x-icon"
        href={`${process.env.CARVANA_APP_CDN_URL}/icons/favicon.ico`}
        crossOrigin=""
      />

      <link
        rel="preload"
        href="https://assets.fastly.carvana.io/fonts/BrandonText-Light.woff2"
        crossOrigin=""
        as="font"
        type="font/woff2"
      />
      <link
        rel="preload"
        href="https://assets.fastly.carvana.io/fonts/BrandonText-Bold.woff2"
        crossOrigin=""
        as="font"
        type="font/woff2"
      />
      <link
        rel="preload"
        href="https://assets.fastly.carvana.io/fonts/BrandonText-Regular.woff2"
        crossOrigin=""
        as="font"
        type="font/woff2"
      />
      <script
        key="structured-data"
        type="application/ld+json"
        // ref from https://nextjs.org/learn/seo/rendering-and-ranking/metadata - Structured Data and Json-LD section
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={SetFAQStructuredData()}
      />
      <script
        // eslint-disable-next-line react/no-danger -- entities will be created unless dangerously set
        dangerouslySetInnerHTML={{
          __html: `
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
            });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${gtm}');
          `
        }}
      />
      <script
        // eslint-disable-next-line react/no-danger -- entities will be created unless dangerously set
        dangerouslySetInnerHTML={{
          __html: `
            (function(h,o,u,n,d) {
              h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)},addAction:function(){}}
              d=o.createElement(u);d.async=1;d.src=n
              n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)
            })(window,document,'script','https://www.datadoghq-browser-agent.com/us1/v6/datadog-rum.js','DD_RUM')
            window.DD_RUM.onReady(function() {
              window.DD_RUM.init({
                applicationId: 'c0e19456-086b-48eb-b954-ea6abf661dcf',
                clientToken: 'pub1d8c8908f469710592832a49e98cf269',
                defaultPrivacyLevel: 'mask-user-input',
                env: '${process.env.CARVANA_APP_ENVIRONMENT}',
                service: 'stc-home-ui',
                sessionSampleRate: 100,
                sessionReplaySampleRate: 100,
                site: 'datadoghq.com',
                version: '${process.env.APP_VERSION}'
              });
              window.DD_RUM.addAction('Rum Initialized');
            })
          `
        }}
      />
    </Head>
    <GlobalStyle />
    <ThemeProvider theme={theme}>
      <Component {...pageProps} />
    </ThemeProvider>
  </>
);

export default MyApp;
